import classNames from "classnames";
import * as React from "react";
import "./dnd.scss";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { IconButton, Input, useAlert } from "uxp/components";
import { containsExpressions, generateUUID, hasProperty, hasValue, parseValue } from "../../util";
import { IContextProvider } from "../../uxp";
import {
    addToPath,
    extractPath,
    findCardTemplate,
    getEditorFields,
    getFieldByIdOrKey,
    getUIFields,
    IBaseProperty,
    IPropertyEditorProps,
    ISerializedUIField,
    IUIFieldDefinition,
    IWDDesignModeProps,
    IWidgetTemplateProps,
    PropertyPath
} from "./common";
import { SelectableItem } from "./SelectableItem";
import { Property, PropertyContainer, PropertyGroup, PropertyGroupTitle, PropertyTitle } from "./ui-props/common";
import { useWDDragState } from "./dnd-context";
import { $L } from "../../localize";
import Readonly from "./ReadonlyInput";
import { generateColumns } from "./ui-fields/horizontal-container/HorizontalContainer";
import { ICardWidget } from "../designer/card-widget-types";
import { IDropdownFieldProperty } from "./ui-fields/dropdown/Dropdown";
import { executeAndBind } from "./data-source-picker/datasource";
import { FAIcon as FontAwesomeIcon } from './components/FAIcon';
import { VisibilityProperty } from "./ui-props/VisibilityProperty";
import { IQueryConditionGroup, evaluateConditions } from "./query-builder/QueryBuilder";
import { Conditional } from "./ConditionalComponent";
import { formatFieldPath, IContextData } from "../../hooks/useContextData";
import { MarginProperty } from "./ui-props/MarginProperty";


const cleanAttributeName = (string: string): string => {
    const regExp = /[^a-zA-Z0-9]/gi
    return string.replace(regExp, '')
}

export interface IUseDropArgs {
    /**
     * designer props
     */
    designer?: IWDDesignModeProps,
    /**
     * ui props from the widget
     */
    uiProps: any,
    /**
     * path for the dropable fields (in uiProps)
     */
    propPath?: PropertyPath,
    /**
     * callback on props change
     */
    onChangeFields?: (newFields: ISerializedUIField[]) => void,
    contextData: IContextData
    /**
     * key of the data
     * default is `form`
     * 
     * this will be used to get and set data 
     */
    dataKey?: string
    /**
     * call back when the data is changed
     */
    onChangeData?: (data: { [key: string]: any }) => void,
    /**
     * uxp context
     */
    uxpContext: IContextProvider,
    disabled?: boolean,
    isMobile?: boolean,
    droppableContainerId?: string,
    droppableParentContainerId?: string,
    proxyContainerId?: string // this will be used to filter the common fields using a secondary container id 
}

interface IFieldDropArgs {
    data: any;
    index: number;
    source: string;
    destination: string;
}


let droppableRegister: { [key: string]: (args: IFieldDropArgs) => void } = {};

export function useDrop(dropArgs: IUseDropArgs) {
    let { designer, uiProps, propPath, contextData, onChangeData, onChangeFields, uxpContext, dataKey, droppableContainerId, droppableParentContainerId, disabled } = dropArgs

    const alerts = useAlert()

    if (!propPath) propPath = ['fields']
    if (!dataKey) dataKey = 'form'

    const droppableId = hasValue(droppableContainerId) ? droppableContainerId : `parent-${dataKey}`
    const disableDrop = dropArgs?.disabled || false

    let registeredFileds = getEditorFields();
    let fields: ISerializedUIField[] = extractPath(uiProps, propPath) || [];
    let fieldRef = React.useRef<ISerializedUIField[]>([])

    React.useEffect(() => {
        fieldRef.current = fields
    }, [fields])

    React.useEffect(() => {
        console.log('props.uiProps___props ', uiProps)
    }, [uiProps])

    React.useEffect(() => {
        droppableRegister[droppableId] = onDropField
        return () => {
            console.log('removing', droppableId);
            delete droppableRegister[droppableId];
        }
    }, [droppableId]);

    // dnd-kit droppable for the whole container. `count` (container-relative
    // field count) rides on the data so the single onDragEnd in WidgetEditor can
    // compute an append index without re-deriving the container contents.
    const containerFieldCount = filterItemsByContainerId([...fields], droppableId).length
    const { setNodeRef: setDroppableRef } = useDroppable({
        id: droppableId,
        data: { isContainer: true, containerId: droppableId, count: containerFieldCount },
    })
    const wdDrag = useWDDragState()

    function filterItemsByContainerId(items: ISerializedUIField[], droppableContainerId: string) {
        // fallback for existing widgets 
        // if all fields doesn't have and containerId return all feilds  
        const _itemsWithContainerId = items.filter(i => hasValue(i?.droppableContainerId))
        if (_itemsWithContainerId.length == 0) return items

        if (!hasValue(droppableContainerId)) return items.filter(i => !hasValue(i.droppableContainerId))
        if (hasValue(dropArgs?.proxyContainerId)) return items.filter(i => (hasValue(i.droppableContainerId) && i.droppableContainerId == dropArgs.proxyContainerId))

        // filter by container 
        return items.filter(i => (hasValue(i.droppableContainerId) && i.droppableContainerId == droppableContainerId))
    }


    function pushField(field: ISerializedUIField, destination: string, index: number, droppableId: string, _fields: ISerializedUIField[]) {
        if (_fields.length == 0) {
            _fields.push(field)
            return _fields
        }
        // sort by droppable id 
        _fields.sort((a, b) => (a.droppableContainerId?.localeCompare(b?.droppableContainerId)))

        const itemsInTheContainer = _fields.filter(f => f.droppableContainerId === destination)
        const itemsNotInTheContainer = _fields.filter(f => f.droppableContainerId != destination)

        if (itemsInTheContainer.length > 0) itemsInTheContainer.splice(index, 0, field)
        else itemsInTheContainer.push(field)

        _fields = itemsNotInTheContainer.concat(itemsInTheContainer)

        return _fields
    }


    function updateAndGetFields(): ISerializedUIField[] {
        const __fields = fieldRef.current || []
        const _itemsWithContainerId = __fields.filter(i => hasValue(i?.droppableContainerId))
        if (_itemsWithContainerId.length == 0) {
            // if all fields doesnt have a container id 
            // set the current container id 
            for (const f of __fields) {
                f.droppableContainerId = droppableId
            }

            // finally update the ref in-case we use ref in any other place 
            fieldRef.current = __fields
        }
        return __fields
    }

    // on drop field 
    async function onDropField(args: IFieldDropArgs) {

        let { data, index, source, destination } = args

        let _fields = updateAndGetFields()

        // insert
        // widget fields 
        if (data?.type == 'fieldType') {

            let f = data.data as IUIFieldDefinition<IBaseProperty>;
            let name = `${dataKey}-field-${_fields.length + 1}-${generateUUID()}`
            let fieldId = name
            if (f.isInputField) {
                let inputs = _fields.filter(f => f.isInputField)
                name = `input${inputs.length + 1}`

                // show a popup and get the id 
                let formData = await alerts.form({
                    title: $L('widgetdesigner.fields.name-prompt', 'Give a name for the field'),
                    formStructure: [
                        {
                            name: 'fieldId',
                            label: $L('widgetdesigner.common.name', 'Name'),
                            type: 'string',
                            validate: {
                                required: true
                            },
                            formatter(value: string) {
                                return cleanAttributeName(value)
                            },
                        }
                    ],
                    submitButtonTitle: $L('widgetdesigner.common.done', 'Done'),
                    hideCancelButton: false,
                    cancelButtonTitle: $L('widgetdesigner.common.cancel', 'Cancel')
                })

                // stop processing 
                if (formData === null) return

                fieldId = formData.fieldId
                // update data 
                updateData(name, fieldId, null)
            }
            const fieldProps: any = { ...(f.defaultProps || {}), fieldLabel: { label: fieldId } }
            let item: ISerializedUIField = {
                name: name,
                type: f.id,
                fieldKey: `${f.id}###${f.description}`,
                metaType: f.type,
                props: fieldProps,
                isInputField: f.isInputField || false,
                id: fieldId,
                droppableContainerId: droppableId,
                dataKey: dataKey,
                propPath: propPath,
                disableDrop: disableDrop,
                onChangeFields: dropArgs.onChangeFields
            };

            // this will be used to generate default props 
            // ex - default columns in the horizontal list 
            if (!!f.onDropField) {
                item = f.onDropField(item)
            }

            const updated = pushField(item, destination, index, droppableId, _fields)
            onChangeFields(updated)
            return;
        }

        // mobile sections 
        // TODO :: update width above changes
        if (data?.type == 'mobileField') {

            let f = data.data as any;
            const fId = generateUUID()
            let item = {
                id: fId,
                name: fId,
                type: f.id,
                template: f,
                droppableContainerId: droppableId,
                dataKey: dataKey,
                propPath: propPath,
                disableDrop: disableDrop,
                onChangeFields: dropArgs.onChangeFields,
                props: { ...f.defaultProps || {}, fieldLabel: { label: fId } },  //Object.assign(f.defaultProps || {},{fieldLabel:{label:fieldId}}),
                isInputField: f.isInputField || false,
            };

            // this will be used to generate default props 
            // ex - default columns in the horizontal list 
            if (!!f.onDropField) {
                item = f.onDropField(item)
            }

            _fields.splice(index, 0, item);
            onChangeFields(_fields)
            return;
        }

        if (data?.type == 'widget') {

            let card_widget = data.data as ICardWidget;
            let name = `widget-field-${_fields.length + 1}`
            let fieldId = name

            let item: ISerializedUIField = {
                name: name,
                type: 'card-widget',
                metaType: 'card-widget',
                props: { widgetData: { ...card_widget || {} } },  //Object.assign(f.defaultProps || {},{fieldLabel:{label:fieldId}}),
                isInputField: false,
                id: fieldId,
            };

            // _fields.splice(index, 0, item);
            // onChangeFields(_fields)
            const updated = pushField(item, destination, index, droppableId, _fields)
            onChangeFields(updated)
            return;
        }

        // sort
        if (data?.type == 'field') {
            let { field, remover, source: sourceId, index: sourceIndex } = data.data;

            // remove field form source 
            const remaining = _fields.filter(f => f.id !== field.id)
            // push to destination 
            field.droppableContainerId = destination
            const updated = pushField(field, destination, index, droppableId, remaining)
            onChangeFields(updated)
            return;
        }

        alert('Unknown drop type:' + JSON.stringify(data));
    }

    function onRemoveField(id: string) {
        const updated = [...fieldRef.current].filter((f: any) => f.id !== id)
        onChangeFields(updated)
    }

    function updateData(name: string, id: string, value: any) {
        let _data = { ...contextData }
        if (!dataKey) dataKey = 'form'

        if (!_data) _data = {}
        if (!_data[dataKey]) _data[dataKey] = {}
        if (!_data[dataKey][id]) _data[dataKey][id] = null

        _data[dataKey][id] = value

        onChangeData?.(_data)
    }

    function createCardWidget(widget: ICardWidget, uxpContext: IContextProvider) {
        let cardWidget = findCardTemplate(widget.Template.__template__?.type)

        if (!cardWidget) return <></>

        // `as any`: TemplateRenderer returns ReactNode; createElement wants a
        // component type (matches CanvasArea.tsx's same call).
        return React.createElement(cardWidget?.template as any, {
            designer: null,
            uiProps: widget.Template?.uiProps,
            uxpContext: uxpContext,
            initialSource: null,
            isInnerField: true
        })
    }

    function renderRemoveButton(f: ISerializedUIField) {

        return <IconButton
            className="wd-dnd-remove-field-btn"
            type="delete"
            variant="danger"
            size="small"
            onClick={(e) => {
                e?.preventDefault()
                e?.stopPropagation()
                onRemoveField(f?.id)
            }}
        />
    }

    function generateFields() {

        //  get fields belong to this container
        const _allFields = [...fields]
        const _fields = filterItemsByContainerId([...fields], droppableId)
        return _fields.map((f, i) => {

            const globalIndex = _allFields.findIndex((af: ISerializedUIField) => af.id === f.id)

            let el: JSX.Element = null;

            if (f.type === 'card-widget') {
                let cardWidget = { ...f.props?.widgetData }

                // default props that need to pass to fields
                el = createCardWidget(cardWidget, dropArgs.uxpContext)

                return (
                    <DragWrapper
                        key={f.id}
                        id={f.id}
                        data={{ tag: { type: 'field', data: { field: f, remover: () => onRemoveField(f.id), source: droppableId, index: i } }, containerId: droppableId, index: i }}
                    >
                        <div className="ui-field-wrapper">
                            <SelectableItem
                                designer={dropArgs.designer}
                                props={[
                                    {
                                        id: addToPath(propPath, globalIndex, 'id'),
                                        value: f.id,
                                        definition: { defaults: '', render: FieldCommonPropsEditor },

                                    },
                                    {
                                        id: addToPath(propPath, globalIndex, 'props', 'visibility'),
                                        value: f?.props?.visibility,
                                        definition: VisibilityProperty(f.props.visibility)
                                    }
                                ]}
                                contextData={contextData}
                            >
                                <div>
                                    {el}
                                </div>
                                <IconButton size='small' className="wd-dnd-remove-field-btn"
                                    type='delete'
                                    variant="danger"
                                    onClick={() => onRemoveField(f?.id)}
                                />
                            </SelectableItem>
                        </div>
                    </DragWrapper>
                );
            }

            let instanceId = f.id
            let field = getFieldByIdOrKey(f, registeredFileds);
            if (field) {
                let _props = { ...f.props }

                // default props that need to pass to fields
                _props.uxpContext = uxpContext
                _props.contextData = contextData

                // extra defaults for inputs (form components)
                if (f.isInputField) {
                    let value: any = null
                    if (contextData?.[dataKey]?.[f.id] != undefined) {
                        value = contextData[dataKey][f.id]
                    }
                    else if (f.props?.value != undefined) {
                        value = f.props.value
                    }
                    let onChange = (value: any) => {
                        updateData(f.name, f.id, value)
                    }

                    _props.value = value
                    _props.onChange = onChange
                }

                el = React.createElement(field.field, {
                    mode: dropArgs.designer?.mode,
                    fieldProps: Object.assign({}, _props),
                    uiProps,
                    uxpContext,
                    designer,
                    contextData: contextData,
                    onChangeContextData: onChangeData,
                    propPath,
                    dataKey,
                    droppableParentContainerId,
                    disableDrop,
                    onChangeFields: f.onChangeFields,
                    instanceId: instanceId,
                    index: globalIndex,
                });
            }
            else {
                console.log('field------', f);

                el = <div>Unknown field type: {f.type}</div>;
            }

            // preview on select template or live 
            if (!designer || designer?.mode == 'thumbnail' || designer?.mode == 'live') {
                return <Conditional visible={evaluateConditions(f?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] } as IQueryConditionGroup, contextData)} >
                    <div className="ui-field-wrapper" style={{ margin: `${f.props.margin?.top || 0}px ${f.props.margin?.right || 0}px ${f.props.margin?.bottom || 0}px ${f.props.margin?.left || 0}px` }}>
                        <SelectableItem designer={null} props={[]}>
                            {el}
                        </SelectableItem>
                    </div>
                </Conditional>
            }

            // render - design mode
            return <DragWrapper
                key={f.id}
                id={f.id}
                data={{ tag: { type: 'field', data: { field: f, remover: () => onRemoveField(f.id), source: droppableId, index: i } }, containerId: droppableId, index: i }}
            >
                <div className="ui-field-wrapper">

                    <SelectableItem
                        designer={dropArgs.designer}
                        props={[
                            {
                                id: addToPath(propPath, globalIndex, 'id'),
                                value: f.id,
                                definition: { defaults: '', render: FieldCommonPropsEditor },

                            },
                            {
                                id: addToPath(propPath, globalIndex, 'props', 'visibility'),
                                value: f?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] } as IQueryConditionGroup,
                                definition: VisibilityProperty(f?.props?.visibility || { IsEither: true, Conditions: [], Groups: [] })
                            },
                            {
                                id: addToPath(propPath, globalIndex, 'props'),
                                value: f.props,
                                definition: { defaults: field?.defaultProps, render: field?.propertyEditor }
                            },
                            {
                                id: addToPath(propPath, globalIndex, 'props', 'margin'),
                                value: f?.props?.margin || 0,
                                definition: MarginProperty(f?.props?.margin || { bottom: 0, top: 0, left: 0, right: 0, title: "Margin (in px)", hideRight: true, hideLeft: true })
                            }
                        ]}
                        contextData={contextData}
                        elementId={instanceId}
                        style={{ margin: `${f.props.margin?.top || 0}px ${f.props.margin?.right || 0}px ${f.props.margin?.bottom || 0}px ${f.props.margin?.left || 0}px` }}
                    >
                        {el}
                        {renderRemoveButton(f)}
                    </SelectableItem>
                </div>
            </DragWrapper>;
        });

    }


    if (!dropArgs?.designer || dropArgs?.designer?.mode == 'thumbnail' || dropArgs?.designer?.mode == 'live') {
        return <div className='droppable-area-live'>
            {generateFields()}
        </div>;
    }

    let highlight = dropArgs?.designer?.selectedPanel == 'fields'

    // container is the drop target when the drag is anywhere over it (empty space
    // or one of its fields) — driven by the shared drag state, not dnd-kit isOver,
    // so the highlight persists while hovering a child field (where the field's
    // own droppable would otherwise steal `over`).
    const isDropTarget = !disableDrop && !!wdDrag.activeId && wdDrag.overContainerId === droppableId
    const containerFieldIds = filterItemsByContainerId([...fields], droppableId).map(f => f.id)

    return <div
        ref={disableDrop ? undefined : setDroppableRef}
        className={classNames('droppable-area ', { 'dragging': isDropTarget, 'highlight': highlight })}
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
    >
        <SortableContext items={containerFieldIds} strategy={verticalListSortingStrategy}>
            {generateFields()}
        </SortableContext>
    </div>;
}

interface IDragWrapper {
    /** globally-unique dnd-kit id (the field id). */
    id: string;
    /** payload carried on the draggable: { tag: {type,data}, containerId, index }. */
    data: any;
    children: React.ReactNode;
}

/**
 * Field draggable. Mirrors the uxp TreeView `SortableTreeNode` pattern: a
 * `useSortable` node with `animateLayoutChanges: () => false` and NO applied
 * transform, so siblings stay put while dragging and an explicit drop-line marks
 * where the field will land (drop commits on release via the single onDragEnd in
 * WidgetEditor). Outside a DndContext (live/thumbnail render of a card-widget)
 * dnd-kit hooks no-op, so this is safe there too.
 */
export const DragWrapper: React.FunctionComponent<IDragWrapper> = (props) => {
    const { setNodeRef, listeners, attributes, isDragging } = useSortable({
        id: props.id,
        data: props.data,
        animateLayoutChanges: () => false,
    });
    const drag = useWDDragState();
    const showLine = !!drag.activeId && drag.overId === props.id && drag.activeId !== props.id;

    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className={classNames('wd-dnd-item', { 'wd-dnd-item--dragging': isDragging })}
            style={{ opacity: isDragging ? 0.4 : undefined }}
        >
            {showLine && drag.dropPosition === 'before' && <div className="wd-dnd-drop-line wd-dnd-drop-line--before" />}
            {props.children}
            {showLine && drag.dropPosition === 'after' && <div className="wd-dnd-drop-line wd-dnd-drop-line--after" />}
        </div>
    );
}

export function triggerDrop(droppable: string, item: IFieldDropArgs) {
    droppableRegister[droppable]?.(item);
}

// getAllValuesForDropDown and extractDataFromFields were extracted to ./field-data
// (re-exported below so existing imports keep working).

const FieldCommonPropsEditor: React.FunctionComponent<IPropertyEditorProps<string>> = (props) => {

    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>{$L('widgetdesigner.fields.field-id', 'Field Id')}</PropertyGroupTitle>
            <Property>
                <Readonly
                    value={props.uiProps}
                />
            </Property>
        </PropertyGroup>
    </PropertyContainer>
}


// generateSerializedField and renderField were extracted to ./field-render
// (re-exported below so existing imports keep working).
export * from "./field-data";
export * from "./field-render";